// Recruiter-app i18n runtime: registers bundles, switches the language, and
// feeds the display-locale helpers. Imported by App.tsx and, through a lazy
// chunk outside its shell, the internal app (pages/internal/
// internalRecruiterI18n.ts). The candidate apps must never import this module
// or a namespace module.
import env, { isBackendOverrideAllowed } from 'env';
import { registerSessionLanguageApplier } from 'stores/sessionStore';
import { useSupportUserStore } from 'stores/supportUserStore';
import { registerApiErrorTranslator } from 'utils/apiError';
import {
  getAppLanguage,
  registerAppTranslator,
  setAppLanguage,
  type AppTextValues,
  type CoreKey,
} from 'utils/appLocale';
import { TIMED_OUT, withTimeout } from 'utils/withTimeout';

import i18n, { registerCommonBundles } from '../i18n';
import { getLocaleBundles, type LocaleBundle } from '../i18nLocaleLoader';
import {
  normalizeRecruiterLanguage,
  RECRUITER_ADMIN_NAMESPACES,
  type RecruiterLanguage,
} from './languages';
import {
  useRecruiterLanguageState,
  type RecruiterLanguageFailure,
} from './languageState';
import {
  loadRecruiterBundles,
  loadRecruiterAdminBundles,
  loadRecruiterDayjsLocale,
  loadRecruiterErrorBundles,
  loadRecruiterStaffBundles,
  markInitialRecruiterLanguageApplied,
  RECRUITER_LANGUAGE_TIMEOUT_MS,
  readCachedRecruiterLanguage,
  writeCachedRecruiterLanguage,
  type RecruiterBundle,
  type RecruiterBundleLoadOptions,
} from './localeLoader';
import { startMissingKeyReporting } from './missingKeyReport';
import {
  getRegisteredRecruiterNamespaces,
  onRecruiterNamespaceRegistered,
} from './registry';
import { CORE_NS } from './namespaces/core';

type BundleLoader = (
  language: RecruiterLanguage,
  options?: RecruiterBundleLoadOptions
) => Promise<RecruiterBundle[]>;

export interface RecruiterLanguageDeps {
  /** The startup namespaces, which the language waits for. */
  loadBundles: BundleLoader;
  /** The staff-only namespaces, once requested (see loadStaffNamespaces). */
  loadStaffBundles: BundleLoader;
  /** Registers `language`'s `errors` bundle and the coded API error translator. */
  loadApiErrors: (language: RecruiterLanguage) => Promise<void>;
  /** The candidate `common` bundle, for components shared with those apps. */
  loadCommonBundles: (language: RecruiterLanguage) => Promise<LocaleBundle[]>;
  loadDayjsLocale: (language: RecruiterLanguage) => Promise<void>;
  timeoutMs: number;
}

const registerBundles = (bundles: RecruiterBundle[]): void => {
  for (const { language, namespace, resources } of bundles) {
    // deep + overwrite, so a re-fetch after a deploy replaces stale strings.
    // The loader's objects are never mutated, so i18next need not copy them.
    i18n.addResourceBundle(language, namespace, resources, true, true, {
      skipCopy: true,
    });
  }
};

/**
 * Loads the coded API error translator and `language`'s `errors` bundle. Lazy
 * so the app shell stays small, and outside the bundle timeout so a slow chunk
 * never keeps the UI English. Until both load, and in English, coded errors
 * show the server's English `detail`; error text is read when an error shows,
 * so nothing needs to re-render when they arrive.
 */
const loadApiErrors = async (language: RecruiterLanguage): Promise<void> => {
  const [module, bundles] = await Promise.all([
    import('./apiErrorText'),
    loadRecruiterErrorBundles(language),
  ]);
  registerBundles(bundles);
  registerApiErrorTranslator(module.translateApiErrorBody);
};

const defaultDeps: RecruiterLanguageDeps = {
  loadBundles: loadRecruiterBundles,
  loadStaffBundles: loadRecruiterStaffBundles,
  loadApiErrors,
  loadCommonBundles: getLocaleBundles,
  loadDayjsLocale: loadRecruiterDayjsLocale,
  timeoutMs: RECRUITER_LANGUAGE_TIMEOUT_MS,
};

/**
 * When the staff namespaces load: never (a customer session), with the
 * language's other optional resources (a staff session in the recruiter app,
 * whose staff surfaces show English until they arrive), or with the startup
 * namespaces (the internal app, where every page is a staff page).
 */
let staffNamespaceLoading: 'skip' | 'optional' | 'required' = 'skip';

let latestRequest = 0;

/** The recruiter language currently applied (English until one is). */
export const getActiveRecruiterLanguage = (): RecruiterLanguage =>
  normalizeRecruiterLanguage(getAppLanguage());

/** Re-renders `language` after an optional resource arrives, if still current. */
const refreshIfCurrent = async (
  language: RecruiterLanguage,
  request: number
): Promise<void> => {
  if (
    request === latestRequest &&
    getActiveRecruiterLanguage() === language &&
    i18n.language === language
  ) {
    // Resource additions alone do not notify react-i18next's default
    // subscriptions. Dates also need a render after their locale registers.
    await i18n.changeLanguage(language);
  }
};

const loadOptionalAdminBundles = (
  language: RecruiterLanguage,
  request: number
): void => {
  const namespaces = getRegisteredRecruiterNamespaces().filter((namespace) =>
    (RECRUITER_ADMIN_NAMESPACES as readonly string[]).includes(namespace)
  );
  if (!namespaces.length) return;
  void loadRecruiterAdminBundles(language, undefined, namespaces)
    .then(async (bundles) => {
      if (request !== latestRequest) return;
      registerBundles(bundles);
      await refreshIfCurrent(language, request);
    })
    .catch(() => {
      // swallow-ok: Administrative components retain registered English copy; a later language application retries the evicted bundle.
    });
};

onRecruiterNamespaceRegistered((namespace) => {
  if (!(RECRUITER_ADMIN_NAMESPACES as readonly string[]).includes(namespace))
    return;
  const { requested } = useRecruiterLanguageState.getState();
  if (requested !== 'en') loadOptionalAdminBundles(requested, latestRequest);
});

/** Loads the staff namespaces outside the language's timeout. */
const loadOptionalStaffBundles = (
  language: RecruiterLanguage,
  request: number,
  deps: RecruiterLanguageDeps
): void => {
  void deps
    .loadStaffBundles(language)
    .then(async (bundles) => {
      if (request !== latestRequest) return;
      registerBundles(bundles);
      await refreshIfCurrent(language, request);
    })
    .catch(() => {
      // swallow-ok: Optional staff namespaces already register English; rejected fetches are evicted by the loader and later language application retries.
      // Staff surfaces keep their bundled English strings.
    });
};

/**
 * Optional resources (error text, staff strings, the shared `common` bundle,
 * dates) can improve the active UI without delaying its language.
 */
const loadOptionalResources = (
  language: RecruiterLanguage,
  request: number,
  deps: RecruiterLanguageDeps
): void => {
  const refreshCurrentLanguage = () => refreshIfCurrent(language, request);
  loadOptionalAdminBundles(language, request);

  void deps.loadApiErrors(language).catch(() => {
    // swallow-ok: Optional error translations retain the server detail via extractErrorMessage; loader eviction permits a later language application to retry.
    // Coded errors keep the server's English detail.
  });
  if (staffNamespaceLoading === 'optional') {
    loadOptionalStaffBundles(language, request, deps);
  }
  void deps
    .loadCommonBundles(language)
    .then(async (bundles) => {
      if (request !== latestRequest) return;
      registerCommonBundles(bundles);
      await refreshCurrentLanguage();
    })
    .catch(() => {
      // swallow-ok: Shared common strings retain registered English when their optional locale fails; locale fetch eviction allows later language application to retry.
      // Shared components keep their bundled English strings.
    });
  void deps
    .loadDayjsLocale(language)
    .then(refreshCurrentLanguage)
    .catch(() => {
      // swallow-ok: Mantine date inputs retain dayjs English when the optional locale import fails; later language application retries the import.
      // Missing optional date resources leave dates in English.
    });
};

/** The namespaces a language waits for: startup, plus staff in the internal app. */
const loadRequiredBundles = async (
  language: RecruiterLanguage,
  deps: RecruiterLanguageDeps,
  options?: RecruiterBundleLoadOptions
): Promise<RecruiterBundle[]> => {
  if (staffNamespaceLoading !== 'required') {
    return deps.loadBundles(language, options);
  }
  const groups = await Promise.all([
    deps.loadBundles(language, options),
    deps.loadStaffBundles(language, options),
  ]);
  return groups.flat();
};

/**
 * Makes `language` the active recruiter language and returns the language
 * actually applied. A failed or slow recruiter load shows English and records
 * a recoverable failure. The shared `common` and dayjs bundles load separately
 * so optional resources cannot hold up the recruiter UI.
 * A newer call supersedes an older one still in flight.
 */
export const applyRecruiterLanguage = async (
  language: RecruiterLanguage,
  deps: RecruiterLanguageDeps = defaultDeps,
  options?: RecruiterBundleLoadOptions
): Promise<RecruiterLanguage> => {
  const request = ++latestRequest;
  const previous = useRecruiterLanguageState.getState();
  useRecruiterLanguageState.setState({
    requested: language,
    loading: true,
    failure: previous.requested === language ? previous.failure : null,
  });
  let applied: RecruiterLanguage = language;
  let failure: RecruiterLanguageFailure | null = null;

  if (language !== 'en') {
    loadOptionalResources(language, request, deps);
    try {
      const result = await withTimeout(
        loadRequiredBundles(language, deps, options),
        deps.timeoutMs
      );
      if (result === TIMED_OUT) {
        failure = 'timeout';
      } else if (request === latestRequest) {
        registerBundles(result);
      }
    } catch {
      failure = 'unavailable';
    }
    if (failure) {
      applied = 'en';
    }
  }

  if (request !== latestRequest) {
    return applied;
  }
  if (failure) {
    console.warn(
      `[recruiter-i18n] ${language} bundles ${failure}; showing English`
    );
  }
  // Formatting helpers read the app language during the re-render that
  // changeLanguage triggers, so it is set first.
  setAppLanguage(applied);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = applied;
  }
  await i18n.changeLanguage(applied);
  if (request === latestRequest) {
    useRecruiterLanguageState.setState({ applied, loading: false, failure });
  }
  return applied;
};

/** Retry the current failed preference without changing the saved setting. */
export const retryRecruiterLanguage = async (
  deps: RecruiterLanguageDeps = defaultDeps
): Promise<RecruiterLanguage> => {
  const { requested, applied, loading, failure } =
    useRecruiterLanguageState.getState();
  if (loading || !failure || requested === 'en') return applied;
  return applyRecruiterLanguage(requested, deps, { retry: true });
};

/**
 * Loads the staff-only namespaces (dev and support tools) for the current
 * language and every later one. Their surfaces show English until they
 * arrive; English itself needs nothing, since the namespace modules register it.
 */
export const loadStaffNamespaces = (
  deps: RecruiterLanguageDeps = defaultDeps
): void => {
  if (staffNamespaceLoading !== 'skip') return;
  staffNamespaceLoading = 'optional';
  // The requested language, not the applied one: a switch still in flight
  // passed its optional resources before this call.
  const { requested } = useRecruiterLanguageState.getState();
  if (requested !== 'en') {
    loadOptionalStaffBundles(requested, latestRequest, deps);
  }
};

/**
 * Applies the language the server reports, unless support has a display override.
 * The server value owns the member's cached language, and null (never
 * chosen, from a backend that omits the effective language) means English. It
 * is compared with the newest requested language, not only the applied one, so
 * it still supersedes a cached language whose bundles are loading.
 */
export const applySessionRecruiterLanguage = async (
  serverLanguage: string | null | undefined,
  deps: RecruiterLanguageDeps = defaultDeps
): Promise<void> => {
  const memberLanguage = normalizeRecruiterLanguage(serverLanguage);
  // Cache the member's language separately from the support-only display override.
  writeCachedRecruiterLanguage(memberLanguage);
  const support = useSupportUserStore.getState();
  const language =
    (support.isSupportUser && support.uiLanguageOverride) || memberLanguage;
  const { requested, applied, loading, failure } =
    useRecruiterLanguageState.getState();
  if (
    language === requested &&
    (loading ||
      (failure &&
        i18n.language === applied &&
        getActiveRecruiterLanguage() === applied))
  ) {
    return;
  }
  if (
    !loading &&
    !failure &&
    language === getActiveRecruiterLanguage() &&
    // A candidate-style ?lang= on a recruiter URL can switch i18next on its
    // own (i18n.ts) without going through applyRecruiterLanguage.
    i18n.language === language
  ) {
    return;
  }
  await applyRecruiterLanguage(language, deps);
};

const translateCore = (
  key: CoreKey,
  defaultValue: string,
  values?: AppTextValues
): string => i18n.t(key, { ...values, ns: CORE_NS, defaultValue });

let initialized = false;

/**
 * Boots recruiter i18n: lets the shared formatting helpers translate, then
 * applies the language cached by the last session so the first paint is
 * already in it. The entry holds its first render on the handshake this
 * resolves (bounded by its own timeout).
 */
export const initRecruiterI18n = (
  deps: RecruiterLanguageDeps = defaultDeps
): void => {
  if (initialized) return;
  initialized = true;
  registerAppTranslator(translateCore);
  startMissingKeyReporting();
  const cached = readCachedRecruiterLanguage();
  if (cached === 'en' && i18n.language === 'en') {
    markInitialRecruiterLanguageApplied();
    return;
  }
  void applyRecruiterLanguage(cached, deps).finally(
    markInitialRecruiterLanguageApplied
  );
};

/**
 * The recruiter app's staff surfaces: dev tools and the backend selector render
 * outside production, the support tools for support users.
 */
const loadStaffNamespacesForStaffSessions = (): void => {
  if (
    env.IS_LOCAL ||
    isBackendOverrideAllowed() ||
    useSupportUserStore.getState().isSupportUser
  ) {
    loadStaffNamespaces();
    return;
  }
  const unsubscribe = useSupportUserStore.subscribe((state) => {
    if (!state.isSupportUser) return;
    unsubscribe();
    loadStaffNamespaces();
  });
};

export interface RecruiterI18nBootOptions {
  /** `required` for the internal app; the recruiter app loads them for staff sessions. */
  staffNamespaces?: 'staffSessions' | 'required';
}

/**
 * Boots recruiter i18n for an app: applies the cached language, lets session
 * verification apply the signed-in user's language before the session is
 * marked ready, and decides when the staff namespaces load.
 */
export const bootRecruiterI18n = ({
  staffNamespaces = 'staffSessions',
}: RecruiterI18nBootOptions = {}): void => {
  if (staffNamespaces === 'required') {
    // Set before the cached language applies, so its first load includes them.
    staffNamespaceLoading = 'required';
  }
  initRecruiterI18n();
  registerSessionLanguageApplier(applySessionRecruiterLanguage);
  if (staffNamespaces === 'staffSessions') {
    loadStaffNamespacesForStaffSessions();
  }
};
